import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Alert,
  Snackbar,
  useTheme,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { getUser } from '../../../store/slices/userSlice';
import { getClients } from '../../../store/slices/clientsSlice';
import { canViewControlCenter, allRoleLabels, userRoles, formatRoleLabel, isManagement } from '../../../utils/permissions';
import { GET } from '../../../configs/api';
import { api } from '../../../utils/constants';
import JobDetailsDialog from '../jobs/JobDetailsDialog';
import UserDetailsDialog from '../users/UserDetailsDialog';
import JobsSummaryPanel from './JobsSummaryPanel';
import JobsKpiJobsDialog from './JobsKpiJobsDialog';
import JobsStatusPanel from './JobsStatusPanel';
import TechniciansPanel from './TechniciansPanel';
import RecentActivityPanel from './RecentActivityPanel';
import CreatedJobsChart from './CreatedJobsChart';
import TopClientsPanel from './TopClientsPanel';
import TopDispatchersPanel from './TopDispatchersPanel';
import ControlCenterClientFilter from './ControlCenterClientFilter';
import ControlCenterGlobalFilter from './ControlCenterGlobalFilter';
import ControlCenterSavedSearchesBar from './ControlCenterSavedSearchesBar';
import {
  loadControlCenterFilters,
  saveControlCenterFilters,
  getRangeLabel,
} from './controlCenterUtils';
import {
  captureFilterSnapshot,
  clearDefaultSearch,
  deleteSearch,
  findSearchById,
  getDefaultSearch,
  loadControlCenterSavedSearches,
  pruneSavedSearchesAgainstClients,
  renameSearch,
  sanitizeFilterSnapshot,
  setDefaultSearch,
  upsertSearch,
  arraysEqualById,
} from './ControlCenterSavedSearchesHelper';

export default function ControlCenter() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const user = useSelector(getUser);
  const companyId = user?.company_id ?? null;
  const hasPermission = canViewControlCenter(user);
  const showAccessNotes = isManagement(user);
  const clientsState = useSelector(getClients);
  // Ready only after a successful load — error payloads set loading=false with clients=[].
  const clientsReady = clientsState?.loading === false && !clientsState?.error;
  const defaultSearchAppliedRef = useRef(false);
  const loadedCompanyIdRef = useRef(companyId);

  const clientOptions = useMemo(
    () => (clientsState?.clients || []).map((c) => ({
      id: c.id,
      client_name: c.client_name || 'Unknown',
    })),
    [clientsState?.clients],
  );

  const [companyName, setCompanyName] = useState('Company');
  const [filters, setFilters] = useState(() => loadControlCenterFilters(companyId));
  const { range, customDateStart, customDateEnd, selectedClients } = filters;
  const [selectedStatus, setSelectedStatus] = useState(null);

  const [savedSearchesStorage, setSavedSearchesStorage] = useState(() => loadControlCenterSavedSearches(companyId));
  const [activeSavedSearchId, setActiveSavedSearchId] = useState(null);

  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [overviewError, setOverviewError] = useState(null);

  const [jobs, setJobs] = useState([]);
  const [jobsTotal, setJobsTotal] = useState(null);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [jobsError, setJobsError] = useState(null);

  const [technicians, setTechnicians] = useState(null);
  const [techniciansLoading, setTechniciansLoading] = useState(true);
  const [techniciansError, setTechniciansError] = useState(null);

  const [activity, setActivity] = useState(null);
  const [activityLoading, setActivityLoading] = useState(true);
  const [activityError, setActivityError] = useState(null);

  const [topClients, setTopClients] = useState(null);
  const [topClientsLoading, setTopClientsLoading] = useState(true);
  const [topClientsError, setTopClientsError] = useState(null);

  const [topDispatchers, setTopDispatchers] = useState(null);
  const [topDispatchersLoading, setTopDispatchersLoading] = useState(true);
  const [topDispatchersError, setTopDispatchersError] = useState(null);

  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetailsError, setUserDetailsError] = useState(null);
  const [userDetailsLoadingId, setUserDetailsLoadingId] = useState(null);
  const [kpiMetric, setKpiMetric] = useState(null);
  const [kpiClientId, setKpiClientId] = useState(null);
  const [kpiClientName, setKpiClientName] = useState(null);
  const [kpiDispatcherId, setKpiDispatcherId] = useState(null);
  const [kpiDispatcherName, setKpiDispatcherName] = useState(null);

  const rangeLabel = getRangeLabel(range, customDateStart, customDateEnd);

  // Custom range is ready with start-only, end-only, or both (open-ended sides).
  const isCustomRangeReady = range !== 'custom' || Boolean(customDateStart || customDateEnd);
  const clientIdsParam = selectedClients.length > 0
    ? selectedClients.map((c) => c.id).join(',')
    : undefined;

  const sharedQueryParams = useMemo(() => {
    const params = { range };
    if (range === 'custom') {
      if (customDateStart) params.dateFrom = customDateStart;
      if (customDateEnd) params.dateTo = customDateEnd;
    }
    if (clientIdsParam) params.clientIds = clientIdsParam;
    return params;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, customDateStart, customDateEnd, clientIdsParam]);

  const updateFilters = useCallback((patch) => {
    setFilters((prev) => {
      const next = { ...prev, ...patch };
      saveControlCenterFilters(companyId, next);
      return next;
    });
    setActiveSavedSearchId(null);
  }, [companyId]);

  // Reload company-scoped filters / saved searches when the active company changes.
  useEffect(() => {
    if (loadedCompanyIdRef.current === companyId) return;
    loadedCompanyIdRef.current = companyId;
    defaultSearchAppliedRef.current = false;
    setActiveSavedSearchId(null);
    setSelectedStatus(null);
    setFilters(loadControlCenterFilters(companyId));
    setSavedSearchesStorage(loadControlCenterSavedSearches(companyId));
  }, [companyId]);

  useEffect(() => {
    if (!hasPermission || !user?.id) return undefined;
    let cancelled = false;
    const loadCompany = async () => {
      const res = await GET(api.getCompanyProfile);
      if (cancelled || res.error) return;
      const name = res.data?.company?.name;
      if (name) setCompanyName(name);
    };
    loadCompany();
    return () => {
      cancelled = true;
    };
  }, [hasPermission, user?.id, companyId]);

  // After the live client list loads for this company: drop inaccessible clients from
  // the header filter + this company's saved searches, then apply the default once.
  useEffect(() => {
    if (!hasPermission || !clientsReady || !companyId) return;
    if (loadedCompanyIdRef.current !== companyId) return;

    setFilters((prev) => {
      const nextClients = sanitizeFilterSnapshot(
        {
          range: prev.range,
          customDateStart: prev.customDateStart,
          customDateEnd: prev.customDateEnd,
          selectedClients: prev.selectedClients,
          selectedStatus: null,
        },
        { clientOptions },
      ).selectedClients;

      if (arraysEqualById(prev.selectedClients, nextClients)) return prev;
      const next = { ...prev, selectedClients: nextClients };
      saveControlCenterFilters(companyId, next);
      return next;
    });

    const pruned = pruneSavedSearchesAgainstClients(
      companyId,
      loadControlCenterSavedSearches(companyId),
      clientOptions,
    );
    setSavedSearchesStorage(pruned.storage);

    if (!defaultSearchAppliedRef.current) {
      defaultSearchAppliedRef.current = true;
      const defaultSearch = getDefaultSearch(pruned.storage);
      if (defaultSearch) {
        const snapshot = sanitizeFilterSnapshot(defaultSearch.filters, { clientOptions });
        const nextFilters = {
          range: snapshot.range,
          customDateStart: snapshot.customDateStart,
          customDateEnd: snapshot.customDateEnd,
          selectedClients: snapshot.selectedClients,
        };
        setFilters(nextFilters);
        saveControlCenterFilters(companyId, nextFilters);
        setSelectedStatus(snapshot.selectedStatus != null ? Number(snapshot.selectedStatus) : null);
        setActiveSavedSearchId(defaultSearch.id);
      }
    }
  }, [hasPermission, clientsReady, clientOptions, companyId]);

  const fetchOverview = useCallback(async () => {
    if (!isCustomRangeReady) {
      setOverviewLoading(false);
      return;
    }
    setOverviewLoading(true);
    setOverviewError(null);
    const res = await GET(api.getControlCenterOverview, sharedQueryParams);
    if (res.error || res.status !== 200) {
      setOverviewError(res.message || 'Failed to load overview');
      setOverviewLoading(false);
      return;
    }
    setOverview(res.data);
    setOverviewLoading(false);
  }, [sharedQueryParams, isCustomRangeReady]);

  const fetchJobs = useCallback(async () => {
    if (!isCustomRangeReady) {
      setJobsLoading(false);
      return;
    }
    setJobsLoading(true);
    setJobsError(null);
    const res = await GET(api.getControlCenterJobs, { ...sharedQueryParams, limit: 500 });
    if (res.error || res.status !== 200) {
      setJobsError(res.message || 'Failed to load jobs');
      setJobsLoading(false);
      return;
    }
    setJobs(res.data?.jobs || []);
    setJobsTotal(typeof res.data?.total === 'number' ? res.data.total : null);
    setJobsLoading(false);
  }, [sharedQueryParams, isCustomRangeReady]);

  const fetchTechnicians = useCallback(async () => {
    if (!isCustomRangeReady) {
      setTechniciansLoading(false);
      return;
    }
    setTechniciansLoading(true);
    setTechniciansError(null);
    const res = await GET(api.getControlCenterTechnicians, sharedQueryParams);
    if (res.error || res.status !== 200) {
      setTechniciansError(res.message || 'Failed to load technicians');
      setTechniciansLoading(false);
      return;
    }
    setTechnicians(res.data);
    setTechniciansLoading(false);
  }, [sharedQueryParams, isCustomRangeReady]);

  const fetchActivity = useCallback(async () => {
    if (!isCustomRangeReady) {
      setActivityLoading(false);
      return;
    }
    setActivityLoading(true);
    setActivityError(null);
    const res = await GET(api.getControlCenterActivity, { ...sharedQueryParams, limit: 50 });
    if (res.error || res.status !== 200) {
      setActivityError(res.message || 'Failed to load activity');
      setActivityLoading(false);
      return;
    }
    setActivity(res.data);
    setActivityLoading(false);
  }, [sharedQueryParams, isCustomRangeReady]);

  // Top Clients: owner/GM/accountant — API ignores clientIds (company-wide).
  // Admin — API scopes to user_client_access ∩ header clientIds, so pass shared params.
  const fetchTopClients = useCallback(async () => {
    if (!isCustomRangeReady) {
      setTopClientsLoading(false);
      return;
    }
    setTopClientsLoading(true);
    setTopClientsError(null);
    const res = await GET(api.getControlCenterTopClients, sharedQueryParams);
    if (res.error || res.status !== 200) {
      setTopClientsError(res.message || 'Failed to load top clients');
      setTopClientsLoading(false);
      return;
    }
    setTopClients(res.data);
    setTopClientsLoading(false);
  }, [sharedQueryParams, isCustomRangeReady]);

  const fetchTopDispatchers = useCallback(async () => {
    if (!isCustomRangeReady) {
      setTopDispatchersLoading(false);
      return;
    }
    setTopDispatchersLoading(true);
    setTopDispatchersError(null);
    const res = await GET(api.getControlCenterTopDispatchers, sharedQueryParams);
    if (res.error || res.status !== 200) {
      setTopDispatchersError(res.message || 'Failed to load top dispatchers');
      setTopDispatchersLoading(false);
      return;
    }
    setTopDispatchers(res.data);
    setTopDispatchersLoading(false);
  }, [sharedQueryParams, isCustomRangeReady]);

  const closeKpiDialog = useCallback(() => {
    setKpiMetric(null);
    setKpiClientId(null);
    setKpiClientName(null);
    setKpiDispatcherId(null);
    setKpiDispatcherName(null);
  }, []);

  const kpiQueryParams = useMemo(() => {
    const params = { ...sharedQueryParams };
    if (kpiClientId) params.clientIds = kpiClientId;
    if (kpiDispatcherId) params.dispatcherId = kpiDispatcherId;
    return params;
  }, [sharedQueryParams, kpiClientId, kpiDispatcherId]);

  useEffect(() => {
    // total_jobs is all-time and does not need a date window.
    if (!isCustomRangeReady && kpiMetric && kpiMetric !== 'total_jobs') {
      closeKpiDialog();
    }
  }, [isCustomRangeReady, kpiMetric, closeKpiDialog]);

  useEffect(() => {
    if (!hasPermission) return;
    fetchOverview();
  }, [hasPermission, fetchOverview]);

  useEffect(() => {
    if (!hasPermission) return;
    fetchJobs();
  }, [hasPermission, fetchJobs]);

  useEffect(() => {
    if (!hasPermission) return;
    fetchTechnicians();
  }, [hasPermission, fetchTechnicians]);

  useEffect(() => {
    if (!hasPermission) return;
    fetchActivity();
  }, [hasPermission, fetchActivity]);

  useEffect(() => {
    if (!hasPermission) return;
    fetchTopClients();
  }, [hasPermission, fetchTopClients]);

  useEffect(() => {
    if (!hasPermission) return;
    fetchTopDispatchers();
  }, [hasPermission, fetchTopDispatchers]);

  const getCurrentFilterSnapshot = useCallback(() => captureFilterSnapshot({
    range,
    customDateStart,
    customDateEnd,
    selectedClients,
    selectedStatus,
  }), [range, customDateStart, customDateEnd, selectedClients, selectedStatus]);

  const applyFilterSnapshot = useCallback((snapshot) => {
    const sanitized = sanitizeFilterSnapshot(snapshot, { clientOptions });
    const nextFilters = {
      range: sanitized.range,
      customDateStart: sanitized.customDateStart,
      customDateEnd: sanitized.customDateEnd,
      selectedClients: sanitized.selectedClients,
    };
    setFilters(nextFilters);
    saveControlCenterFilters(companyId, nextFilters);
    setSelectedStatus(sanitized.selectedStatus != null ? Number(sanitized.selectedStatus) : null);
  }, [clientOptions, companyId]);

  const handleResetFilters = useCallback(() => {
    applyFilterSnapshot({});
    setActiveSavedSearchId(null);
  }, [applyFilterSnapshot]);

  const handleApplySavedSearch = useCallback((searchId) => {
    const search = findSearchById(savedSearchesStorage, searchId);
    if (!search) return;
    applyFilterSnapshot(search.filters);
    setActiveSavedSearchId(search.id);
  }, [savedSearchesStorage, applyFilterSnapshot]);

  const handleSaveSearch = useCallback(({ name, setAsDefault }) => {
    if (!companyId) return;
    const { storage: nextStorage, searchId } = upsertSearch(companyId, savedSearchesStorage, {
      name,
      filters: getCurrentFilterSnapshot(),
      setAsDefault,
    });
    setSavedSearchesStorage(nextStorage);
    setActiveSavedSearchId(searchId);
  }, [companyId, savedSearchesStorage, getCurrentFilterSnapshot]);

  const handleDeleteSavedSearch = useCallback((searchId) => {
    if (!companyId) return;
    const nextStorage = deleteSearch(companyId, savedSearchesStorage, searchId);
    setSavedSearchesStorage(nextStorage);
    if (activeSavedSearchId === searchId) setActiveSavedSearchId(null);
  }, [companyId, savedSearchesStorage, activeSavedSearchId]);

  const handleRenameSavedSearch = useCallback((searchId, name) => {
    if (!companyId) return;
    setSavedSearchesStorage(renameSearch(companyId, savedSearchesStorage, searchId, name));
  }, [companyId, savedSearchesStorage]);

  const handleSetDefaultSavedSearch = useCallback((searchId) => {
    if (!companyId) return;
    setSavedSearchesStorage(setDefaultSearch(companyId, savedSearchesStorage, searchId));
  }, [companyId, savedSearchesStorage]);

  const handleClearDefaultSavedSearch = useCallback(() => {
    if (!companyId) return;
    setSavedSearchesStorage(clearDefaultSearch(companyId, savedSearchesStorage));
  }, [companyId, savedSearchesStorage]);

  if (!hasPermission) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Access Denied — Control Center is available to owners, general managers, accountants, and admins.
        </Alert>
      </Box>
    );
  }

  const handleViewFullJob = (job) => {
    if (!job?.id) return;
    setSelectedJob({ id: job.id });
  };

  const handleKpiMetricClick = (metric) => {
    if (!metric || !isCustomRangeReady) return;
    setKpiClientId(null);
    setKpiClientName(null);
    setKpiDispatcherId(null);
    setKpiDispatcherName(null);
    setKpiMetric(metric);
  };

  const handleTopClientJobsClick = (metric, client) => {
    if (!metric || !client?.clientId) return;
    if (metric !== 'total_jobs' && !isCustomRangeReady) return;
    setKpiDispatcherId(null);
    setKpiDispatcherName(null);
    setKpiClientId(client.clientId);
    setKpiClientName(client.clientName || null);
    setKpiMetric(metric);
  };

  const handleTopDispatcherJobsClick = (metric, dispatcher) => {
    if (!metric || !dispatcher?.dispatcherId || !isCustomRangeReady) return;
    setKpiClientId(null);
    setKpiClientName(null);
    setKpiDispatcherId(dispatcher.dispatcherId);
    setKpiDispatcherName(dispatcher.fullName || null);
    setKpiMetric(metric);
  };

  const handleTopDispatcherUserClick = async (dispatcher) => {
    if (!isManagement(user) || !dispatcher?.dispatcherId) return;
    if (userDetailsLoadingId) return;
    setUserDetailsError(null);
    setUserDetailsLoadingId(dispatcher.dispatcherId);
    const res = await GET(api.getUserById(dispatcher.dispatcherId));
    setUserDetailsLoadingId(null);
    if (res.error || res.status !== 200 || !res.data?.id) {
      setUserDetailsError(res.message || 'Failed to load user details');
      return;
    }
    setSelectedUser(res.data);
  };

  const roles = userRoles(user);
  const roleLabelsText = allRoleLabels(user);

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        mx: 'auto',
        px: { xs: 1.5, sm: 2.5, md: 3, xl: 4 },
        py: { xs: 1.5, sm: 2 },
        pb: { xs: 4, sm: 5 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
          mb: { xs: 1.5, sm: 2 },
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 15, color: 'text.secondary', mb: 0.5 }}>
            Control Center
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: { xs: 18, sm: 20 }, fontWeight: 500 }}>
              {companyName}
            </Typography>
            {roles.length > 0 ? (
              roles.map((role) => (
                <Chip
                  key={role}
                  label={formatRoleLabel(role)}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 22,
                    fontSize: 11,
                    fontWeight: 600,
                    borderColor: isDark ? '#334155' : '#e7e7e7',
                  }}
                />
              ))
            ) : (
              <Chip
                label={roleLabelsText || 'Owner'}
                size="small"
                variant="outlined"
                sx={{
                  height: 22,
                  fontSize: 11,
                  fontWeight: 600,
                  borderColor: isDark ? '#334155' : '#e7e7e7',
                }}
              />
            )}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          mb: { xs: 1.5, sm: 2 },
          px: { xs: 1, sm: 1.5 },
          py: 0.5,
          border: '1px solid',
          borderColor: isDark ? '#334155' : '#e7e7e7',
          borderRadius: '10px',
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          <ControlCenterClientFilter
            clients={clientOptions}
            selectedClients={selectedClients}
            onChange={(next) => updateFilters({ selectedClients: next })}
          />
          <ControlCenterSavedSearchesBar
            savedSearches={savedSearchesStorage.searches}
            defaultSearchId={savedSearchesStorage.defaultId}
            activeSearchId={activeSavedSearchId}
            onApplySearch={handleApplySavedSearch}
            onSaveSearch={handleSaveSearch}
            onDeleteSearch={handleDeleteSavedSearch}
            onRenameSearch={handleRenameSavedSearch}
            onSetDefault={handleSetDefaultSavedSearch}
            onClearDefault={handleClearDefaultSavedSearch}
            onResetFilters={handleResetFilters}
            isDarkMode={isDark}
          />
        </Box>
        <ControlCenterGlobalFilter
          range={range}
          customDateStart={customDateStart}
          customDateEnd={customDateEnd}
          onChange={({ range: nextRange, customDateStart: nextStart, customDateEnd: nextEnd }) => updateFilters({
            range: nextRange,
            customDateStart: nextStart,
            customDateEnd: nextEnd,
          })}
          isDarkMode={isDark}
        />
      </Box>

      {!isCustomRangeReady && (
        <Alert severity="info" sx={{ mb: { xs: 1.5, sm: 2 } }}>
          Select a start date, an end date, or both to apply the custom range.
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 } }}>
        <JobsSummaryPanel
          data={overview}
          loading={overviewLoading}
          error={overviewError}
          rangeLabel={rangeLabel}
          onMetricClick={isCustomRangeReady ? handleKpiMetricClick : undefined}
        />

        <JobsStatusPanel
          statusCounts={overview?.statusCounts || []}
          jobs={jobs}
          jobsTotal={jobsTotal}
          loading={jobsLoading || overviewLoading}
          error={jobsError}
          selectedStatus={selectedStatus}
          onStatusChange={setSelectedStatus}
          onViewFullJob={handleViewFullJob}
        />

        <TopClientsPanel
          data={topClients}
          loading={topClientsLoading}
          error={topClientsError}
          onJobsClick={handleTopClientJobsClick}
          showAccessNotes={showAccessNotes}
        />

        <TopDispatchersPanel
          data={topDispatchers}
          loading={topDispatchersLoading}
          error={topDispatchersError}
          canOpenUser={showAccessNotes}
          onUserClick={handleTopDispatcherUserClick}
          onJobsClick={isCustomRangeReady ? handleTopDispatcherJobsClick : undefined}
          showAccessNotes={showAccessNotes}
        />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: { xs: 1.5, sm: 2 },
            alignItems: 'stretch',
          }}
        >
          <RecentActivityPanel
            data={activity}
            loading={activityLoading}
            error={activityError}
            rangeLabel={rangeLabel}
            onOpenJob={handleViewFullJob}
          />
          <CreatedJobsChart
            data={overview}
            loading={overviewLoading}
            error={overviewError}
            rangeLabel={rangeLabel}
          />
        </Box>

        <TechniciansPanel
          data={technicians}
          loading={techniciansLoading}
          error={techniciansError}
          rangeLabel={rangeLabel}
        />
      </Box>

      <JobsKpiJobsDialog
        open={Boolean(kpiMetric)}
        metric={kpiMetric}
        rangeLabel={rangeLabel}
        queryParams={kpiQueryParams}
        clientName={kpiClientName}
        dispatcherName={kpiDispatcherName}
        onClose={closeKpiDialog}
        onOpenJob={handleViewFullJob}
      />

      {selectedJob && (
        <JobDetailsDialog
          selectedJob={selectedJob}
          setSelectedJob={setSelectedJob}
          user={user}
        />
      )}

      {UserDetailsDialog({ selectedUser, setSelectedUser, user })}

      <Snackbar
        open={Boolean(userDetailsError)}
        autoHideDuration={4000}
        onClose={() => setUserDetailsError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setUserDetailsError(null)} sx={{ width: '100%' }}>
          {userDetailsError}
        </Alert>
      </Snackbar>
    </Box>
  );
}
